import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { Header } from "@/components/Header";
import { BottomNav } from "@/components/BottomNav";
import { authGuard } from "@/lib/authGuard";
import { useAppStore } from "@/lib/store";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileText, Clock, CheckCircle, AlertCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { getMyBids, type MyBid } from "@/lib/api";

export const Route = createFileRoute("/my-bids")({
  head: () => ({
    meta: [
      { title: "My Tests — PataTasks" },
      {
        name: "description",
        content: "Track your test submissions, payment status, and testing activity.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  beforeLoad: async () => {
    const store = await authGuard();
    if (!store.isAuthenticated) {
      throw redirect({ to: "/login" });
    }
  },
  component: MyBidsPage,
});

type ApplicationStatus = "submitted" | "pending" | "active" | "review";

const STATUS_CONFIG: Record<
  ApplicationStatus,
  {
    label: string;
    variant: "default" | "secondary" | "destructive" | "outline";
    icon: typeof Clock;
  }
> = {
  submitted: { label: "Submitted", variant: "secondary", icon: Send },
  pending: { label: "Pending", variant: "outline", icon: Clock },
  active: { label: "Active", variant: "default", icon: CheckCircle },
  review: { label: "In Review", variant: "destructive", icon: AlertCircle },
};

function MyBidsPage() {
  const [applications, setApplications] = useState<MyBid[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadBids = async () => {
      try {
        const data = await getMyBids();
        setApplications(data.applications);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to load applications");
      } finally {
        setIsLoading(false);
      }
    };

    loadBids();
  }, []);

  const getStatusConfig = (status: string) => {
    const normalized = status.toLowerCase() as ApplicationStatus;
    return (
      STATUS_CONFIG[normalized] || {
        label: status.charAt(0).toUpperCase() + status.slice(1),
        variant: "outline" as const,
        icon: Clock,
      }
    );
  };

  return (
    <div className="bg-background pb-20 md:pb-0">
      <div className="mx-auto max-w-7xl px-4 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">My Tests</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track your test submissions, payment status, and testing activity.
          </p>
        </div>

        {isLoading ? (
          <div className="grid gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="border-blue-200">
                <CardContent className="py-6">
                  <div className="animate-pulse space-y-3">
                    <div className="h-5 bg-slate-200 rounded w-3/4" />
                    <div className="h-4 bg-slate-200 rounded w-1/2" />
                    <div className="h-4 bg-slate-200 rounded w-1/4" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : applications.length === 0 ? (
          <Card className="border-blue-200">
            <CardContent className="py-12 text-center">
              <FileText className="mx-auto h-12 w-12 text-slate-300 mb-3" />
              <p className="text-sm text-muted-foreground font-medium">
                You haven't accepted any tests yet.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Browse available tests and submit your first application.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {applications.map((app) => {
              const config = getStatusConfig(app.status);
              const Icon = config.icon;
              return (
                <Card key={app.id} className="hover:shadow-md transition-shadow border-blue-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-base font-semibold leading-tight line-clamp-2">
                          {app.gigTitle}
                        </CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                          Applied on{" "}
                          {new Date(app.date).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </p>
                      </div>
                      <Badge variant={config.variant} className="shrink-0">
                        <Icon className="mr-1 h-3.5 w-3.5" />
                        {config.label}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="flex items-center justify-between pt-0">
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {new Date(app.date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </span>
                      <span className="font-semibold text-foreground">
                        $
                        {app.amount.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                    {app.amount > 0 && (
                      <Badge
                        variant="default"
                        className="shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        <CheckCircle className="mr-1 h-3.5 w-3.5" />
                        Paid
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
}
